import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import Loader from '../Loader';
import Alert from '../Alert';
import serverClient from '../../api/ServerClient';
import { DeleteToken } from '../../auth/Auth';
import { useNavigate } from 'react-router-dom';
import {
    getDateTimeInTimezone,
    getUserTimezone,
    bengaliToEnglishNumber,
    formatAmountInput,
} from '../../util/helper';
import { useLanguage } from '../../context/LanguageContext';
import { getAccounts } from '../../util/cacheManager';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useSheetTouchIsolation from '../../hooks/useSheetTouchIsolation';
import AttachmentFilePreview from '../Attachments/AttachmentFilePreview';
import { ATTACHMENT_ACCEPT, validateAttachmentFile } from '../../util/attachmentHelper';
import { FiCamera, FiTrash2, FiAlertCircle } from 'react-icons/fi';
import '../../styles/AccountToggleOptions.css';

const AddTransaction = ({
    show,
    onClose,
    onTransactionAdd,
    transactionType,
    setShowToast,
    voicePrefilledData,
}) => {
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedToAccount, setSelectedToAccount] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [accounts, setAccounts] = useState(null);
    const [categories, setCategories] = useState(null);
    const [amount, setAmount] = useState('');
    const overlayRef = useRef(null);
    const formRef = useRef(null);
    const fileInputRef = useRef(null);

    // Add-mode: pending local File object (not yet uploaded, uploaded right after transaction creation)
    const [pendingFile, setPendingFile] = useState(null);
    const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);
    const [imgError, setImgError] = useState(false);
    const [showDeleteAttachmentConfirm, setShowDeleteAttachmentConfirm] = useState(false);

    useBodyScrollLock(show);
    useSheetTouchIsolation(overlayRef, show);

    // Revoke blob URL on unmount / close
    useEffect(() => {
        return () => {
            if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
        };
    }, [pendingPreviewUrl]);

    const handlePendingFileChange = (e) => {
        const file = e.target.files[0];
        e.target.value = null;
        if (!file) return;
        const errorMessage = validateAttachmentFile(file, t);
        if (errorMessage) {
            setError(errorMessage);
            return;
        }
        setError('');
        if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
        setImgError(false);
        setShowDeleteAttachmentConfirm(false);
        setPendingFile(file);
        setPendingPreviewUrl(URL.createObjectURL(file));
    };

    const handleRemovePendingFile = () => {
        URL.revokeObjectURL(pendingPreviewUrl);
        setPendingFile(null);
        setPendingPreviewUrl(null);
        setImgError(false);
        setShowDeleteAttachmentConfirm(false);
    };

    const requestAttachmentDelete = () => {
        setError('');
        setShowDeleteAttachmentConfirm(true);
    };

    const cancelAttachmentDelete = () => setShowDeleteAttachmentConfirm(false);

    const confirmAttachmentDelete = () => {
        handleRemovePendingFile();
    };

    const accountOptions = useMemo(() => {
        const mappedAccounts =
            accounts?.map((account) => ({ value: account.id, label: account.name })) || [];

        if (transactionType === 'transfer') {
            return [...mappedAccounts, { value: 0, label: 'Out Of Wallet' }];
        }

        return mappedAccounts;
    }, [accounts, transactionType]);

    let categoryOptions;
    if (transactionType === 'expense') {
        categoryOptions = categories
            ?.filter((category) => category.type === 'Expense')
            .map((category) => ({
                value: category.id,
                label: category.name,
            }));
    } else if (transactionType === 'income') {
        categoryOptions = categories
            ?.filter((category) => category.type === 'Income')
            .map((category) => ({
                value: category.id,
                label: category.name,
            }));
    } else {
        categoryOptions = categories?.map((category) => ({
            value: category.id,
            label: category.name,
        }));
    }

    useEffect(() => {
        if (!selectedAccount && accountOptions.length > 0) {
            const cashOption = accountOptions.find((opt) => opt.label === 'Cash');
            if (cashOption) {
                setSelectedAccount(cashOption);
            }
        }
    }, [accountOptions, selectedAccount]);

    let title = 'Transaction';
    let buttonLabel = 'addTransaction'; // Default label
    if (transactionType === 'transfer') {
        title = t('transferMoney');
        buttonLabel = 'transfer';
    } else if (transactionType === 'expense') {
        title = t('addExpense');
        buttonLabel = 'addExpense';
    } else if (transactionType === 'income') {
        title = t('addIncome');
        buttonLabel = 'addIncome';
    } else if (transactionType === 'withdraw') {
        title = t('withdrawMoney');
        buttonLabel = 'withdraw';
    }

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoading(true);
                const [fetchedAccounts, categoriesResponse] = await Promise.all([
                    getAccounts(),
                    serverClient.get('/api/user/categories'),
                ]);
                setAccounts(fetchedAccounts);
                setCategories(categoriesResponse?.data?.categories);
            } catch (e) {
                if (e.response?.status === 401) {
                    DeleteToken();
                    setError(t('userNotAuthorized'));
                    navigate('/login');
                } else {
                    setError(t('somethingWentWrong'));
                }
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [navigate, t]);

    // Pre-fill form with voice data if available
    useEffect(() => {
        if (voicePrefilledData && categories && accounts) {
            // Pre-fill amount - apply minus sign for expenses, just like manual form
            if (voicePrefilledData.amount) {
                const absoluteAmount = Math.abs(voicePrefilledData.amount).toString();
                if (voicePrefilledData.transactionType === 'expense') {
                    setAmount(`-${absoluteAmount}`); // Add minus sign for expense
                } else {
                    setAmount(absoluteAmount); // No minus for income/transfer/withdraw
                }
            }

            // Pre-fill category (only for expense/income)
            if (
                voicePrefilledData.categoryId &&
                categories.length > 0 &&
                (voicePrefilledData.transactionType === 'expense' ||
                    voicePrefilledData.transactionType === 'income')
            ) {
                const category = categories.find((cat) => cat.id === voicePrefilledData.categoryId);
                if (category) {
                    setSelectedCategory({
                        value: category.id,
                        label: category.name,
                    });
                }
            }

            // Pre-fill from account
            if (voicePrefilledData.accountId && accounts.length > 0) {
                const account = accounts.find((acc) => acc.id === voicePrefilledData.accountId);
                if (account) {
                    setSelectedAccount({
                        value: account.id,
                        label: account.name,
                    });
                }
            }

            // Pre-fill to account (for transfer/withdraw types)
            if (
                voicePrefilledData.toAccountId &&
                accounts.length > 0 &&
                (voicePrefilledData.transactionType === 'transfer' ||
                    voicePrefilledData.transactionType === 'withdraw')
            ) {
                const toAccount = accounts.find((acc) => acc.id === voicePrefilledData.toAccountId);
                if (toAccount) {
                    setSelectedToAccount({
                        value: toAccount.id,
                        label: toAccount.name,
                    });
                }
            }
        }
    }, [voicePrefilledData, categories, accounts]);

    const handleTransactionFormSubmit = async (event) => {
        event.preventDefault();

        if (!selectedAccount) {
            setError(t('selectAccount'));
            return;
        }

        setLoading(true);

        const transactionTime = document.getElementById('date_time').value;
        const note = document.getElementById('remarks')?.value;
        const payee = document.getElementById('payee')?.value;
        // const paymentType = document.getElementById("payment_type").value;

        const payload = {
            amount: parseFloat(amount),
            account_id: selectedAccount.value,
            category_id: selectedCategory?.value,
            transaction_type: transactionType === 'withdraw' ? 'transfer' : transactionType,
            is_lending: !!payee,
            lending_to: payee,
            transaction_time: transactionTime,
            note,
            to_account_id: selectedToAccount?.value,
            timezone: getUserTimezone(),
            // payee,
            // paymentType
        };

        try {
            setLoading(true);
            const response = await serverClient.post('/api/user/transaction', payload);
            if (response.status === 201 || response.status === 200) {
                const newTransactionId = response.data?.transaction_id;
                if (pendingFile && newTransactionId) {
                    try {
                        const formData = new FormData();
                        formData.append('attachment', pendingFile);
                        await serverClient.post(
                            `/api/user/transaction/${newTransactionId}/attachment`,
                            formData,
                            { headers: { 'Content-Type': 'multipart/form-data' } }
                        );
                    } catch (_) {
                        // non-fatal: transaction created, attachment failed silently
                    }
                }
                setShowToast(true);
                onTransactionAdd();
                onClose();
            }
        } catch (error) {
            if (error.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
            } else if (error.response?.status === 400) {
                setError(error.response?.data?.message);
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Loader loading={loading} />
            <div
                ref={overlayRef}
                className={`bottom-sheet-overlay ${show ? 'open' : ''}`}
                onClick={onClose}
                role="presentation"
                aria-hidden={!show}
            >
                <div
                    className="auth-form bottom-sheet add-money-modal"
                    onClick={(e) => e.stopPropagation()}
                    tabIndex={-1}
                >
                    <div className="sheet-header p-2">
                        <button
                            className="close-btn dynamic-color-text"
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            &times;
                        </button>
                        <h2 className="dynamic-color-text text-end">{title}</h2>
                    </div>
                    <div className="scrollable-content">
                        <section className="section-b-space auth-form p-2">
                            <div className="sheet-body">
                                {error && <Alert alert={'danger'}> {error} </Alert>}

                                <form ref={formRef} onSubmit={handleTransactionFormSubmit}>
                                    <div className="form-group">
                                        <label className="form-label">{t('amount')}</label>
                                        <div className="form-input mb-3">
                                            <input
                                                required
                                                id="amount"
                                                type="text"
                                                inputMode="decimal"
                                                className="form-control"
                                                placeholder={t('enterAmount')}
                                                value={(() => {
                                                    if (amount === '' || amount === '-') return '';
                                                    // Get the raw number without minus sign for display
                                                    const rawValue = amount.replace(/^-/, '');
                                                    return formatAmountInput(rawValue, language);
                                                })()}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    // Convert Bengali to English and remove commas
                                                    const cleanedValue = bengaliToEnglishNumber(
                                                        value
                                                    )
                                                        .replace(/,/g, '')
                                                        .replace(/[^\d.]/g, '');

                                                    if (
                                                        cleanedValue === '' ||
                                                        cleanedValue === '.'
                                                    ) {
                                                        if (transactionType === 'expense') {
                                                            setAmount('-');
                                                        } else {
                                                            setAmount('');
                                                        }
                                                        return;
                                                    }

                                                    // Prevent multiple decimal points
                                                    const parts = cleanedValue.split('.');
                                                    if (parts.length > 2) {
                                                        return;
                                                    }

                                                    // Limit integer part to 9 digits
                                                    if (parts[0].length > 9) {
                                                        return;
                                                    }

                                                    if (transactionType === 'expense') {
                                                        setAmount(`-${cleanedValue}`);
                                                    } else {
                                                        setAmount(cleanedValue);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="account" className="form-label mb-2">
                                            {' '}
                                            {transactionType === 'transfer' ||
                                            transactionType === 'withdraw'
                                                ? t('fromAccount')
                                                : t('account')}
                                        </label>
                                        <div className="d-flex form-input gap-2">
                                            <Select
                                                id="account"
                                                required
                                                options={accountOptions}
                                                value={selectedAccount}
                                                onChange={setSelectedAccount}
                                                placeholder={t('selectAccount')}
                                                isSearchable
                                                classNamePrefix="react-select"
                                                styles={{
                                                    container: (base) => ({
                                                        ...base,
                                                        width: '100%',
                                                    }),
                                                    control: (base) => ({
                                                        ...base,
                                                        width: '100%',
                                                    }),
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {(transactionType === 'transfer' ||
                                        transactionType === 'withdraw') && (
                                        <div className="form-group">
                                            <label htmlFor="to_account" className="form-label mb-2">
                                                {t('toAccount')}
                                            </label>
                                            <div className="d-flex form-input gap-2">
                                                <Select
                                                    id="to_account"
                                                    required
                                                    options={accountOptions}
                                                    value={selectedToAccount}
                                                    onChange={setSelectedToAccount}
                                                    placeholder={t('selectAccount')}
                                                    isSearchable
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        container: (base) => ({
                                                            ...base,
                                                            width: '100%',
                                                        }),
                                                        control: (base) => ({
                                                            ...base,
                                                            width: '100%',
                                                        }),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {(transactionType === 'income' ||
                                        transactionType === 'expense') && (
                                        <div className="form-group">
                                            <label htmlFor="category" className="form-label mb-2">
                                                {t('category')}
                                            </label>
                                            <div className="d-flex gap-2">
                                                <Select
                                                    id="category"
                                                    required
                                                    options={categoryOptions}
                                                    value={selectedCategory}
                                                    onChange={setSelectedCategory}
                                                    placeholder={t('selectCategory')}
                                                    isSearchable
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        container: (base) => ({
                                                            ...base,
                                                            width: '100%',
                                                        }),
                                                        control: (base) => ({
                                                            ...base,
                                                            width: '100%',
                                                        }),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {selectedCategory?.value === 20 && (
                                        <div className="form-group">
                                            <label htmlFor="payee" className="form-label mb-2">
                                                {t('payee')}
                                            </label>
                                            <div className="form-input mb-3">
                                                <input
                                                    id="payee"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t('payeeName')}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {selectedCategory?.value === 21 && (
                                        <div className="form-group">
                                            <label htmlFor="payee" className="form-label mb-2">
                                                {t('borrowedFrom')}
                                            </label>
                                            <div className="form-input mb-3">
                                                <input
                                                    id="payee"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t('borrowedFromName')}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label htmlFor="date_time" className="form-label mb-2">
                                            {t('dateTime')}
                                        </label>
                                        <div className="form-input mb-3">
                                            <input
                                                id="date_time"
                                                type="datetime-local"
                                                className="form-control"
                                                placeholder="Select date & time"
                                                defaultValue={getDateTimeInTimezone()}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="remarks" className="form-label mb-2">
                                            {t('remarks')}
                                        </label>
                                        <div className="form-input mb-3">
                                            <input
                                                id="remarks"
                                                type="text"
                                                className="form-control"
                                                placeholder={t('noteOrRemarks')}
                                            />
                                        </div>
                                    </div>

                                    {/* Attachment – add mode: pending file, uploaded after creation */}
                                    <div className="form-group">
                                        <label className="form-label">
                                            {t('transactionAttachment')}
                                        </label>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept={ATTACHMENT_ACCEPT}
                                            style={{ display: 'none' }}
                                            onChange={handlePendingFileChange}
                                        />
                                        {pendingPreviewUrl ? (
                                            <div className="account-attachment-preview">
                                                <div className="attachment-thumb-wrapper">
                                                    {imgError ? (
                                                        <div className="attachment-broken">
                                                            <FiAlertCircle
                                                                size={32}
                                                                className="broken-icon"
                                                            />
                                                            <span className="broken-text">
                                                                {t('imageUnavailable')}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <AttachmentFilePreview
                                                            url={pendingPreviewUrl}
                                                            filename={pendingFile?.name}
                                                            imgError={imgError}
                                                            onImgError={() => setImgError(true)}
                                                            className="attachment-thumb"
                                                            alt="Transaction attachment"
                                                            clickable={false}
                                                        />
                                                    )}
                                                </div>
                                                <div className="attachment-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm attachment-btn-change"
                                                        onClick={() =>
                                                            fileInputRef.current?.click()
                                                        }
                                                    >
                                                        <FiCamera size={13} />{' '}
                                                        {t('changeAttachment')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm attachment-btn-delete"
                                                        onClick={requestAttachmentDelete}
                                                    >
                                                        <FiTrash2 size={13} />
                                                    </button>
                                                </div>
                                                {showDeleteAttachmentConfirm && (
                                                    <div
                                                        className="attachment-delete-confirm"
                                                        role="alertdialog"
                                                        aria-live="polite"
                                                    >
                                                        <p className="delete-confirm-text">
                                                            {t('confirmAttachmentDelete')}
                                                        </p>
                                                        <div className="delete-confirm-actions">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={cancelAttachmentDelete}
                                                            >
                                                                {t('cancel')}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                onClick={confirmAttachmentDelete}
                                                            >
                                                                {t('deleteAttachment')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="attachment-upload-placeholder"
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                <FiCamera size={22} className="attach-icon" />
                                                <span className="attach-label">
                                                    {t('uploadAttachment')}
                                                </span>
                                                <span className="attach-hint">
                                                    JPEG · PNG · WebP · PDF · TXT · max 4 MB
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </section>
                    </div>
                    {/* Fixed the Add Transaction button */}
                    <div className="fixed-footer">
                        <button
                            type="submit"
                            onClick={() => formRef.current?.requestSubmit()}
                            className="btn theme-btn successfully w-100"
                        >
                            {t(buttonLabel)}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddTransaction;
